// imports
import { Address } from 'abitype';
import { TransactionReceipt } from 'viem';
import {
  getAccount, waitForTransaction, getNetwork, switchNetwork
} from 'wagmi/actions';
import MainNet from '../abi/mainnet.json';
import Sepolia from '../abi/sepolia.json';
import { TRAITS } from '../../../constants';
import { IContractProps } from '../wagmi/types';
import ArbNova from '../abi/arbitrum_nova.json';
import ArbGeorli from '../abi/arbitrum_goerli.json';
import { Chain, HeroNFT, HeroTraits } from '../types';
import { readContract, writeContract } from '../wagmi';

// interfaces / types / consts
const MAX_SUPPLY = 9999;
const RANDOM_OFFSET = 8675;
type HeroTokens = { [key: string]: HeroNFT };
const L1Chain = (process.env.REACT_APP_ENVIRONMENT === 'prod' || process.env.REACT_APP_ENVIRONMENT === 'stage') ? MainNet : Sepolia;
const L2Chain = (process.env.REACT_APP_ENVIRONMENT === 'prod' || process.env.REACT_APP_ENVIRONMENT === 'stage') ? ArbNova : ArbGeorli;
const CHAIN_IDS = {
  L1: Number(L1Chain.chainId),
  L2: Number(L2Chain.chainId)
} as { [key: string]: number };
const CONTRACT = {
  L1: L1Chain.contracts.Immortals,
  L2: L2Chain.contracts.Immortals_Childchain
} as { [key: string]: IContractProps };

// private methods - contract views
export function getL2Tokens(): Promise<HeroTokens> {
  const { address } = getAccount();
  const contract = CONTRACT.L2;
  return readContract({
    args: [address],
    abi: contract.abi,
    chainId: CHAIN_IDS.L2,
    address: contract.address,
    functionName: 'getTokensForAddress'
  }).then((data) => {
    const [indicies, locked, lockedUntil] = data as [][];
    return indicies.reduce((acc, iTokenId, idx) => {
      const tokenId = Number(iTokenId);
      acc[tokenId] = {
        tokenId, chain: 'L2', locked: Boolean(locked[idx]), lockedUntil: Number(lockedUntil[idx])
      };
      return acc;
    }, {} as HeroTokens);
  });
}

export function getL1Tokens(tokenIds: number[]): Promise<HeroTokens> {
  const contract = CONTRACT.L1;
  return readContract({
    args: [tokenIds],
    abi: contract.abi,
    chainId: CHAIN_IDS.L1,
    address: contract.address,
    functionName: 'batchLocked'
  }).then((data) => {
    const locked = data as boolean[];
    return tokenIds.reduce((acc, tokenId, idx) => {
      acc[tokenId] = {
        tokenId, chain: 'L1', locked: locked[idx], lockedUntil: 0
      };
      return acc;
    }, {} as HeroTokens);
  });
}

// exports - views
export function getOwned(L1TokenIds: number[]): Promise<HeroNFT[]> {
  let heroTokens: HeroTokens = {};
  return getL1Tokens(L1TokenIds)
    .then((L1Tokens) => {
      heroTokens = L1Tokens;
      return getL2Tokens();
    })
    .then((L2Tokens) => Object.keys(heroTokens).map((tokenId) => {
      if (heroTokens[tokenId].locked) {
        const L2Token = L2Tokens[tokenId];
        if (L2Token) {
          heroTokens[tokenId].chain = 'L2';
          heroTokens[tokenId].locked = L2Token.locked;
          heroTokens[tokenId].lockedUntil = L2Token.lockedUntil;
        } else {
          heroTokens[tokenId].locked = false;
          heroTokens[tokenId].chain = 'Transit';
        }
      } else {
        const L2Token = L2Tokens[tokenId];
        if (L2Token) {
          heroTokens[tokenId].locked = false;
          heroTokens[tokenId].chain = 'Transit';
        }
      }
      const offsetTokenId = ((Number(tokenId) + RANDOM_OFFSET) % MAX_SUPPLY) + 1;
      const traits = TRAITS[`${offsetTokenId}`] as HeroTraits;
      return { ...heroTokens[tokenId], ...traits };
    }));
}

// exports - writers
export function burn(tokenIds: number[]): Promise<TransactionReceipt> {
  const contract = CONTRACT.L2;
  const args = tokenIds.length ? [tokenIds] : [];
  return writeContract({
    args,
    abi: contract.abi,
    address: contract.address,
    functionName: tokenIds.length ? 'devBurn' : 'burnAll'
  }).then((data) => waitForTransaction({ hash: data!.hash }));
}
export function awardHeroes(address: Address, countOrTokenIds: number | number[]): Promise<TransactionReceipt> {
  const chain: Chain = typeof countOrTokenIds === 'number' ? 'L1' : 'L2';
  const contract = CONTRACT[chain];
  return writeContract({
    abi: contract.abi,
    address: contract.address,
    args: [address, countOrTokenIds],
    functionName: chain === 'L1' ? 'safeMint' : 'devMint'
  }).then((data) => waitForTransaction({ hash: data!.hash }));
}
export function switchChains(tokenIds: number[], targetChain: Chain): Promise<boolean> {
  const contract = CONTRACT.L1;
  const args: [number[] | number] = [tokenIds];
  let functionName = 'unlockTokens';
  if (targetChain === 'L2') {
    args.push(0);
    functionName = 'lockTokens';
  }
  const { chain } = getNetwork();
  const L1ChainId = CHAIN_IDS.L1;
  const id = chain?.id || 0;
  const promise = (id === L1ChainId) ? Promise.resolve() : switchNetwork({ chainId: L1ChainId });
  return promise.then(() => writeContract({
    args,
    functionName,
    abi: contract.abi,
    chainId: L1ChainId,
    address: contract.address
  })).then((data) => waitForTransaction({ hash: data!.hash })).then(() => true);
}

// exports - default
export default {
  getOwned, burn, awardHeroes, switchChains
};
